import { useState } from "react";
import { styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "radix-ui";
import { useTranslations } from "@acto/web-common/locales";
import type { CustomerContext } from "@acto/taskpane/core/chat";
import type { ChatHistory } from "@acto/taskpane/core/domain";
import { SHEET_Z_INDEX } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { useBodyScrollLock } from "@acto/taskpane/field-sales-2/components/primitives/useBodyScrollLock";
import { ChatHeader } from "@acto/taskpane/field-sales-2/pages/chat/ChatHeader";
import { ChatInputDock } from "@acto/taskpane/field-sales-2/pages/chat/ChatInputDock";
import { MarkdownRenderer } from "@acto/taskpane/field-sales-2/pages/chat/markdown/MarkdownRenderer";
import {
  AssistantRow,
  MessageBody,
  MessageViewport,
  UserBubble,
  UserRow,
} from "@acto/taskpane/field-sales-2/pages/chat/messageBubbles";
import { LockedChatNotice } from "./LockedChatNotice";

export const HistoryOverlay = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: SHEET_Z_INDEX,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  background: theme.fieldSalesTwo.colors.background,
}));

interface ReadOnlyChatViewerProps {
  customer: CustomerContext;
  chatHistory: ChatHistory;
  onClose: () => void;
  onStartNewChat: (message: string) => void;
}

/**
 * A past conversation, rendered from stored messages rather than the assistant runtime. It cannot
 * be resumed, so anything typed into the composer opens a fresh chat instead.
 */
export function ReadOnlyChatViewer({ customer, chatHistory, onClose, onStartNewChat }: ReadOnlyChatViewerProps) {
  const tChat = useTranslations("chat");
  const [value, setValue] = useState("");

  useBodyScrollLock(true);

  const handleSend = () => {
    const trimmed = value.trim();
    if (trimmed) onStartNewChat(trimmed);
  };

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <HistoryOverlay aria-describedby={undefined}>
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{tChat("chatHistory")}</Dialog.Title>
          </VisuallyHidden.Root>
          <MessageViewport>
            <ChatHeader customer={customer} onBack={onClose} />
            {chatHistory.messages.map((message, index) =>
              message.role === "user" ? (
                <UserRow key={index}>
                  <UserBubble role="article" aria-label="user message">
                    {message.content}
                  </UserBubble>
                </UserRow>
              ) : (
                <AssistantRow key={index} role="article" aria-label="assistant message">
                  <MessageBody>
                    <MarkdownRenderer content={message.content} />
                  </MessageBody>
                </AssistantRow>
              ),
            )}
          </MessageViewport>
          <ChatInputDock
            value={value}
            placeholder={tChat("chatPlaceholder")}
            notice={<LockedChatNotice />}
            onChange={setValue}
            onSend={handleSend}
          />
        </HistoryOverlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
